import type { PositionMinutes } from '@statsbomb/kitbag-ai-summary';
import type { BaseOption, PlayedPosition } from '@statsbomb/parachute-types';

export const annotateOptionsWithCounts = (
  options: BaseOption<string>[],
  counts: Map<string, number>,
): BaseOption<string>[] =>
  options.map(option => {
    const count = counts.get(option.value);
    return count ? { ...option, label: `${option.label} - ${count}` } : option;
  });

// Raw minutes at each position (percentage of the total). Unlike positionPercentagesTo90sPlayed this
// does not divide by 90 — the AI summary agent needs actual minutes to check its ≥300 threshold.
export const positionPercentagesToMinutes = (
  playerPositions: PlayedPosition[],
  playerTotalMinutes: number,
): PositionMinutes[] =>
  playerPositions.map(({ position, percentage }) => ({
    position,
    minutes: Number(((percentage / 100) * playerTotalMinutes).toFixed(1)),
  }));

export const positionPercentagesTo90sPlayed = (
  playerPositions: PlayedPosition[],
  playerTotalMinutes: number,
): Record<string, number> =>
  playerPositions.reduce((acc, { position, percentage }) => {
    const played90s = ((percentage / 100) * playerTotalMinutes) / 90;
    return {
      ...acc,
      [position]: Number(played90s.toFixed(1)),
    };
  }, {});

export const formatPlayerName = (nickname: string | null, displayName: string) =>
  nickname ? `${nickname} (${displayName})` : displayName;

export const normalisePositionPercentagesTo100 = (data: PlayedPosition[]): PlayedPosition[] => {
  // Calculate the total of the selected percentages
  const total = data.reduce((sum, item) => sum + item.percentage, 0);

  // Scale each percentage to add up to 100%
  return data.map(item => ({
    ...item,
    percentage: (item.percentage / total) * 100,
  }));
};
